// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2026 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { Sans12, spacing } from "@recidiviz/design-system";
import { observer } from "mobx-react-lite";
import { rem } from "polished";
import styled from "styled-components";

import { palette } from "~design-system";

import { Resident } from "../../../../WorkflowsStore/Resident";
import { Incarceration } from "../../../WorkflowsJusticeInvolvedPersonProfile";
import { Heading } from "../../../WorkflowsJusticeInvolvedPersonProfile/Heading";
import { OpportunitiesAccordion } from "../../../WorkflowsJusticeInvolvedPersonProfile/OpportunitiesAccordion";
import { WorkflowsPreviewModal } from "../../../WorkflowsPreviewModal";
import { useAllCaseloadsModalContext } from "../AllCaseloadsModalContext";
import { AllCaseloadsTablePresenter } from "../AllCaseloadsTablePresenter";
import { SelectFormContents } from "./SelectFormContents";

const ItemHeader = styled(Sans12)`
  background-color: ${palette.marble2};
  border-bottom: 1px solid ${palette.slate10};
  border-top: 1px solid ${palette.slate10};
  color: ${palette.slate70};
  font-weight: 700;
  margin: 0 -${rem(spacing.md)};
  min-width: 100%;
  padding-top: ${rem(4)};
  padding-bottom: ${rem(4)};
  padding-left: ${rem(16)};
  text-transform: uppercase;
`;

function OverviewContents({
  selectedResident,
}: {
  selectedResident: Resident;
}) {
  return (
    <>
      <ItemHeader>Opportunities</ItemHeader>
      <OpportunitiesAccordion
        showIneligibleOpportunityTypes
        formLinkButton
        person={selectedResident}
      />
      <ItemHeader>Resident Details</ItemHeader>
      <Incarceration resident={selectedResident} />
    </>
  );
}

export const AllCaseloadsPreviewModal = observer(
  function AllCaseloadsPreviewModal({
    presenter,
  }: {
    presenter: AllCaseloadsTablePresenter;
  }) {
    const { selectedResident } = presenter;

    const { currentView } = useAllCaseloadsModalContext();

    if (!selectedResident) return null;

    return (
      <WorkflowsPreviewModal
        isOpen={!!selectedResident}
        pageContent={
          <article>
            <Heading person={selectedResident} />
            {currentView === "OVERVIEW" ? (
              <OverviewContents selectedResident={selectedResident} />
            ) : (
              <SelectFormContents selectedResident={selectedResident} />
            )}
          </article>
        }
      />
    );
  },
);
